import { Injectable } from '@angular/core';
import { JwtHelperService } from '@auth0/angular-jwt';
import {
    ChangePassword,
    Credential,
    ResetPassword
} from '@zlib/data-access/models/user.model';
import { BaseApiService } from '@zlib/services/baseApi.serivces';
import { KeycloakEventTypeLegacy } from 'keycloak-angular';
import { BehaviorSubject, Observable } from 'rxjs';
import { Subject } from 'rxjs/internal/Subject';
import { map } from 'rxjs/operators';
import { Enterprise } from '../data-access/models/enterprise.model';
import { Subscription } from '../data-access/models/subscription.model';
import { PagingData } from '../data-access/request-models/pagination.model';
import { ElectronService } from './electron.service';
import { ToastService } from './toast.service';

export interface Register {
    email: string;
    name: string;
    password: string;
}

export enum ModalAction {
    None,
    Login,
    SignUp
}

export interface LoginResult {
    result?: boolean;
    user?: any;
    message?: string;
}

export interface AuthResponse {
    result: boolean;
    errors?: Error[];
    isLockedOut?: boolean;
    isNotAllowed?: boolean;
    requiresTwoFactor?: boolean;
    isNotConfirmed?: boolean;
    requiredInfo?: boolean;
    token: AccessToken;
    user: Credential;
}

export interface AccessToken {
    accessToken: string;
    expiresIn: number;
}

export interface Error {
    code: string;
    description: string;
}
@Injectable({ providedIn: 'root' })
export class AuthService extends BaseApiService {
    private static END_POINT = {
        signIn: 'auth/login',
        logout: 'auth/logout',
        requestOtp: 'api/accounts/requestOtp',
        verifyOtp: 'api/accounts/verifyOtp',
        profile: 'bo/user/getProfile',
        forgotPassword: 'auth/forgotPassword',
        resetPassword: 'auth/resetPassword',
        setPassword: 'api/accounts/setPassword',
        register: 'api/accounts/register',
        changePassword: 'auth/changePassword',
        subscription: 'v1/subscription/me'
    };

    ModalAction = ModalAction;

    public authedSubject: BehaviorSubject<boolean>;
    public currentUserSubject: BehaviorSubject<Credential>;
    public currentSubscriptionsSubject: BehaviorSubject<Subscription[]>;
    public currentSubscriptionSubject: BehaviorSubject<Subscription>;
    public currentEnterprise: BehaviorSubject<Enterprise>;
    public currentUser: Observable<Credential>;
    public openLogin: BehaviorSubject<ModalAction>;

    public updateProfileSub = new Subject();

    constructor(
        protected readonly jwtHelper: JwtHelperService,
        protected readonly toastService: ToastService,
        protected readonly electronService: ElectronService
    ) {
        super();
        const token = this.localStorageService.loadInfo('token');

        const subscriptions =
            this.localStorageService.loadInfo('subscriptions');
        const enterprise = this.localStorageService.loadInfo('enterprise');
        const subscription = this.localStorageService.loadInfo('subscription');

        let user: Credential | any;

        if (token) {
            try {
                const tokenPayload = this.jwtHelper.decodeToken(token) as any;
                user = new Credential();
                user.id = tokenPayload.sub;
                user.roles = tokenPayload.realm_access.roles;
                user.name = tokenPayload.preferred_username;
            } catch (err) {}
        }

        this.currentUserSubject = new BehaviorSubject<Credential>(user);
        this.currentSubscriptionsSubject = new BehaviorSubject<Subscription[]>(
            subscriptions ? JSON.parse(subscriptions) : null
        );
        this.currentEnterprise = new BehaviorSubject<Enterprise>(
            enterprise ? JSON.parse(enterprise) : null
        );
        this.currentSubscriptionSubject = new BehaviorSubject<Subscription>(
            subscription ? JSON.parse(subscription) : null
        );
        let authed = false;
        try {
            authed = token ? !this.jwtHelper.isTokenExpired(token) : false;
        } catch (err) {
            console.error(err);
        }
        this.authedSubject = new BehaviorSubject<boolean>(authed);

        this.currentUser = this.currentUserSubject.asObservable();
        this.openLogin = new BehaviorSubject<ModalAction>(ModalAction.None);

        this.keycloak.keycloakEvents$.subscribe({
            next: (e) => {
                switch (e.type) {
                    case KeycloakEventTypeLegacy.OnReady:
                        console.log('keycloak is ready');
                        break;
                    case KeycloakEventTypeLegacy.OnAuthSuccess:
                        console.log('keycloak is auth successful');
                        break;
                    case KeycloakEventTypeLegacy.OnTokenExpired:
                        this.authedSubject.next(false);
                        console.log('update refresh token');
                        this.keycloak.updateToken(180).then((result) => {
                            const token =
                                this.keycloak.getKeycloakInstance().token;
                            const idToken =
                                this.keycloak.getKeycloakInstance().idToken;
                            const refreshToken =
                                this.keycloak.getKeycloakInstance()
                                    .refreshToken;

                            console.log(token, refreshToken, idToken);
                            this.authedSubject.next(true);

                            this.localStorageService.setInfo(
                                'refresh_token',
                                refreshToken
                            );

                            this.localStorageService.setInfo(
                                'access_token',
                                token
                            );

                            this.localStorageService.setInfo('token', token);

                            this.localStorageService.setInfo(
                                'id_token',
                                idToken
                            );
                        });
                        break;
                }
            }
        });
    }

    public set currentUserValue(user: Credential) {
        this.currentUserSubject.next(user);
    }

    selectSubscription(sub: Subscription): void {
        this.localStorageService.setInfo('subscription', JSON.stringify(sub));
        this.currentSubscriptionSubject?.next(sub);
    }

    selectEnterprise(item: Enterprise): void {
        this.localStorageService.setInfo('enterprise', JSON.stringify(item));
        this.localStorageService.setInfo('tenant', item.vatId);
        this.currentEnterprise?.next(item);
    }

    getSubscription(): Observable<Subscription[]> {
        return new Observable((observable) => {
            this.get('v1/subscription/me').subscribe({
                next: (res: PagingData<Subscription>) => {
                    this.currentSubscriptionsSubject.next(res.data);
                    if (res.data?.length) {
                        if (!this.currentSubscriptionSubject.value) {
                            this.selectSubscription(res.data[0]);
                        }
                    } else {
                        this.selectSubscription(null);
                    }

                    if (res.data?.length && res.data[0].enterprises.length) {
                        if (!this.currentEnterprise.value) {
                            this.selectEnterprise(res.data[0].enterprises[0]);
                        }
                    } else {
                        this.selectEnterprise(null);
                    }

                    this.localStorageService.setInfo(
                        'subscriptions',
                        JSON.stringify(res.data)
                    );
                    observable.next(res.data);
                },
                error: (err) => {
                    observable.error(err);
                }
            });
        });
    }

    register(callback: string): Promise<void> {
        return this.keycloak.register({
            redirectUri: callback
        });
    }

    login(callback: string): Promise<void> {
        let uri = callback;
        if (this.electronService.isElectronApp) {
            uri = 'http://localhost/keycloak-redirect/#callback';
        }

        return this.keycloak.login({
            redirectUri: uri
        });
    }

    getProfile(callback: string): Observable<AuthResponse> {
        if (!this.keycloak.isLoggedIn()) {
            let uri = callback;
            if (this.electronService.isElectronApp) {
                uri = 'http://localhost/keycloak-redirect/#callback';
            }

            this.keycloak.login({
                redirectUri: uri
            });
            return null;
        }

        // const token = this.localStorageService.loadInfo('access_token');
        // const refreshToken = this.localStorageService.loadInfo('refresh_token');

        return new Observable((observable) => {
            this.keycloak
                .loadUserProfile()
                .then((profile) => {
                    const token = this.keycloak.getKeycloakInstance().token;
                    const idToken = this.keycloak.getKeycloakInstance().idToken;
                    const refreshToken =
                        this.keycloak.getKeycloakInstance().refreshToken;
                    this.authedSubject.next(true);

                    const data = this.jwtHelper.decodeToken(token);
                    this.localStorageService.setInfo('id_token', idToken);
                    this.localStorageService.setInfo('token', token);
                    this.localStorageService.setInfo('access_token', token);
                    this.localStorageService.setInfo(
                        'refresh_token',
                        refreshToken
                    );

                    const user = new Credential();
                    user.id = data.sub;
                    user.roles = data.realm_access.roles;
                    user.name = data.preferred_username;

                    this.currentUserValue = user;
                    observable.next({
                        result: true,
                        user,
                        token: {
                            accessToken: token,
                            expiresIn: this.jwtHelper.getTokenExpirationDate(token).getMilliseconds(),
                        }
                    });
                })
                .catch((err) => {
                    console.error(err);
                    this.keycloak.login();
                    observable.error();
                });
        });
    }

    logout(): Observable<boolean> {
        // remove user from local storage and set current user to null
        this.localStorageService.clearAllLocalStorage();
        this.authedSubject.next(false);
        this.currentUserSubject?.next(null);
        this.currentSubscriptionsSubject?.next(null);
        this.currentEnterprise?.next(null);

        return new Observable((observable) => {
            this.keycloak.logout().then(() => {
                this.keycloak.login();
                observable.next(null);
            });
        });
    }

    getCurrentUser(): Observable<Credential> {
        return this.currentUserSubject.asObservable();
    }

    async isAuthenticated(): Promise<boolean | null> {
        const token = this.localStorageService.loadInfo('token');

        if (!token || token === 'undefined') {
            return false;
        }

        if (!this.jwtHelper.isTokenExpired(token)) {
            this.authedSubject.next(false);
            return true;
        }

        this.keycloak.login();
        return false;
    }

    forgotPassword(userName: string, email: string) {
        const endPoint = AuthService.END_POINT.forgotPassword;
        return this.post(endPoint, { userName, email }).pipe(
            map((res) => {
                if (res) {
                    this.toastService.createSuccessNotification(
                        'login.notification.sent_email_reset_password',
                        ''
                    );
                }
            })
        );
    }

    resetPassword(resetPassword: ResetPassword) {
        const endPoint = AuthService.END_POINT.resetPassword;
        if (!this.jwtHelper.isTokenExpired(resetPassword.token)) {
            return this.post(endPoint, resetPassword).pipe(
                map((response: any) => response)
            );
        }
    }

    setPassword(resetPassword: ResetPassword) {
        const endPoint = AuthService.END_POINT.setPassword;

        return this.post(endPoint, resetPassword).pipe(
            map((response: any) => response)
        );
    }

    changePassword(changePassword: ChangePassword) {
        const endPoint = AuthService.END_POINT.changePassword;
        return this.post(endPoint, changePassword).pipe(
            map((response: any) => response)
        );
    }

    setUserPermission(user: Credential) {
        let userPermissions: string[] = [];
        if (user.roles && user.roles.length) {
            user.roles.forEach((role) => {
                if (role.resourceIds) {
                    userPermissions = [...userPermissions, ...role.resourceIds];
                }
            });
        }
        this.localStorageService.setInfo(
            'user_permissions',
            JSON.stringify(userPermissions)
        );
    }
}
